<template>
  <!-- Full-screen block shown whenever this device isn't cleared to run the
       app. Rendered instead of the router view, so nothing behind it can be
       reached or interacted with. -->
  <div class="device-gate">
    <div class="gate-logo" role="img" aria-label="Popin"></div>

    <div class="gate-card">

      <!-- ── Checking ──────────────────────────────────────────────────── -->
      <template v-if="checking">
        <i class="las la-circle-notch gate-icon spin"></i>
        <h2 class="gate-title">Verifying device</h2>
        <p class="gate-text">Just a moment…</p>
      </template>

      <!-- ── Awaiting approval (device is new to the server) ───────────── -->
      <template v-else-if="status === 'pending'">
        <i class="las la-user-clock gate-icon"></i>
        <h2 class="gate-title">Awaiting approval</h2>
        <p class="gate-text">
          This device has been registered and is waiting for an administrator to
          approve it. Share the device ID below with your administrator.
        </p>
      </template>

      <!-- ── Blocked (server knows it and has it switched off) ─────────── -->
      <template v-else-if="status === 'blocked'">
        <i class="las la-ban gate-icon danger"></i>
        <h2 class="gate-title">Device blocked</h2>
        <p class="gate-text">
          This device is not permitted to use the app. Contact your
          administrator if you believe this is a mistake.
        </p>
      </template>

      <!-- ── Couldn't verify ───────────────────────────────────────────── -->
      <template v-else>
        <i class="las la-exclamation-triangle gate-icon danger"></i>
        <h2 class="gate-title">Device check failed</h2>
        <p class="gate-text">{{ message || 'Unable to verify this device.' }}</p>
      </template>

      <!-- Device ID: the one thing support will ask for, so make it easy to
           read out and to copy. -->
      <div v-if="uuid" class="gate-id">
        <span class="gate-id-label">Device ID</span>
        <code class="gate-id-value">{{ uuid }}</code>
        <button type="button" class="gate-copy" @click="copyId">
          <i class="las" :class="copied ? 'la-check' : 'la-copy'"></i>
          {{ copied ? 'Copied' : 'Copy' }}
        </button>
      </div>

      <button
        v-if="!checking"
        type="button"
        class="gate-retry"
        @click="retry"
      >
        <i class="las la-redo-alt"></i> Check again
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'DeviceGate',

  data() {
    return { copied: false }
  },

  computed: {
    ...mapState('device', ['status', 'message']),
    ...mapGetters('device', { checking: 'isChecking', uuid: 'uuid' }),
  },

  methods: {
    retry() {
      this.$store.dispatch('device/register')
    },

    async copyId() {
      try {
        await navigator.clipboard.writeText(this.uuid)
      } catch (_) {
        // Clipboard API needs a secure context and isn't in every WebView;
        // fall back to a hidden textarea + execCommand.
        const el = document.createElement('textarea')
        el.value = this.uuid
        el.setAttribute('readonly', '')
        el.style.position = 'absolute'
        el.style.left = '-9999px'
        document.body.appendChild(el)
        el.select()
        try { document.execCommand('copy') } catch (__) { /* nothing else to try */ }
        document.body.removeChild(el)
      }
      this.copied = true
      setTimeout(() => { this.copied = false }, 2000)
    },
  },
}
</script>

<style scoped>
.device-gate {
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #000;
  padding: 24px;
  overflow-y: auto;
}

.gate-logo {
  width: min(280px, 70%);
  height: 90px;
  flex-shrink: 0;
  background-color: #e31e25;
  background-image: url('/popin-logo.png');
  background-repeat: no-repeat;
  background-position: center;
  background-size: min(240px, 80%) auto;
  border-radius: 12px;
  margin-bottom: 28px;
}

.gate-card {
  width: 100%;
  max-width: 420px;
  background: #131313;
  border: 1px solid #262626;
  border-radius: 16px;
  padding: 28px 24px;
  text-align: center;
  color: #fff;
}

.gate-icon {
  font-size: 46px;
  color: #e31e25;
  display: block;
  margin-bottom: 12px;
}
.gate-icon.danger { color: #ff5a5f; }

.spin { animation: gate-spin 1s linear infinite; }
@keyframes gate-spin { to { transform: rotate(360deg); } }

.gate-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 8px;
}

.gate-text {
  font-size: 14px;
  line-height: 1.55;
  color: #a8a8a8;
  margin: 0;
}

.gate-id {
  margin-top: 22px;
  padding: 12px;
  background: #0a0a0a;
  border: 1px solid #262626;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.gate-id-label {
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7a7a7a;
}

.gate-id-value {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 13px;
  color: #fff;
  /* Long ids must wrap rather than stretch the card on a narrow phone. */
  word-break: break-all;
}

.gate-copy {
  background: none;
  border: none;
  color: #e31e25;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
}

.gate-retry {
  margin-top: 20px;
  width: 100%;
  padding: 13px;
  border: none;
  border-radius: 10px;
  background: #e31e25;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}
.gate-retry:active { opacity: 0.85; }
</style>
